<template>
  <div class="container mt-4">
    <!-- Builder Interface - Show when no thread exists -->
    <div v-if="!threadId" class="builder-section">
      <!-- Prompt Area -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">Programming Language</h5>
        </div>
        <div class="card-body">
          <div class="row g-2 align-items-center">
            <div class="col-md-3">
              <label class="form-label mb-0">Language</label>
              <select v-model="selectedLanguage" class="form-select">
                <option v-for="lang in languages" :key="lang" :value="lang">{{ lang }}</option>
                <option value="Other">Other...</option>
              </select>
              <input v-if="selectedLanguage === 'Other'" v-model="customLanguage" type="text" class="form-control mt-2"
                placeholder="Enter language">
            </div>
            <div class="col-md-3">
              <label class="form-label mb-0">API Feature</label>
              <select v-model="selectedFeature" class="form-select">
                <option v-for="feature in apiFeatures" :key="feature" :value="feature">{{ feature }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label mb-0">API Key</label>
              <input v-model="apiKey" type="text" class="form-control" placeholder="e.g. YOUR_API_KEY"
                @keydown.enter.prevent>
              <small v-if="!sessionState" class="text-muted">Sign in to see your API key.</small>
            </div>
          </div>
        </div>
      </div>
      <template v-if="showQueryDefnBuilder">
        <!-- Locations Editor -->
        <div class="card mb-2">
          <div class="card-header">
            <h5 class="mb-0">Locations</h5>
          </div>
          <div class="card-body">
            <LocationsEditor :parentLocations="currentPackage.locations || []" :sessionState="sessionState"
              @update:locations="updateLocations" />

            <!-- Option for handling multiple locations -->
            <div class="mt-3" :class="{ 'opacity-40': !currentPackage.locations || currentPackage.locations.length <= 1 }" 
                :style="{ pointerEvents: (!currentPackage.locations || currentPackage.locations.length <= 1) ? 'none' : 'auto' }">
              <label class="form-label"><strong>Multiple Locations Handling</strong></label>
              <select v-model="multiLocationStrategy" class="form-select" 
                      :disabled="!currentPackage.locations || currentPackage.locations.length <= 1">
                <option value="loop">Loop Single Location API in Code (recommended for most cases)</option>
                <option value="timelinemulti">Use Timeline Multi Endpoint (where necessary, please note that it may have different rate limits)</option>
              </select>
              <small class="text-muted">
                <span v-if="!currentPackage.locations || currentPackage.locations.length === 0">
                  Add at least one location to enable this option.
                </span>
                <span v-else-if="currentPackage.locations.length === 1">
                  Add more locations to enable multiple location handling options.
                </span>
                <span v-else>
                  Choose whether to generate code that loops through each location using the single location API or uses the Timeline Multi Endpoint.
                </span>
              </small>
            </div>
          </div>
        </div>

        <!-- Data options for all goals -->
        <div class="card mb-2">
          <div class="card-header">
            <h5 class="mb-0">Date Range and Data Options</h5>
          </div>
          <div class="card-body">
            <div class="row g-2">
              <div class="col-md-3">
                <label class="form-label mb-0">Date Range</label>
                <DateRangeChooser class="" :queryDefinition="currentPackage.params" @change:daterange="setDateRange">
                </DateRangeChooser>
              </div>

              <div class="col-md-3">
                <label class="form-label mb-0">Unit group</label>
                <UnitGroupDropdown v-model="queryDefn.unitGroup" />
              </div>
              <div class="col-md-6">
                <ContentTypeChooser :queryDefinition="queryDefn" />
              </div>

              <div class="col-md-4">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="showTimelineQueryEditor = true">
                  <i class="bi bi-bell me-1"></i>
                  Advanced options
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
      <div class="card mb-2">
        <div class="card-header">
          <h5 class="mb-0">Weather API Code Generation Prompt</h5>
        </div>
        <div class="card-body">
          <form @submit.prevent="sendMessage">
            <div class="">
              <textarea v-model="userPrompt" class="form-control mb-2" rows="5" @keydown.enter.prevent></textarea>
              <small class="text-muted">This prompt is auto-generated and editable.</small>
            </div>
            <div class="mt-2 text-end">
              <button class="btn btn-primary" type="submit" :disabled="isThinking">
                <span v-if="!isThinking">Generate Code Sample</span>
                <span v-else>
                  <span class="spinner-border spinner-border-sm me-1"></span>
                  Processing...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Conversation Interface - Show when thread exists -->
    <div v-else class="conversation-section">
      <!-- Configuration Summary -->
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Code generation request</h6>
          <button class="btn btn-sm btn-outline-primary" @click="clearThread">
            <i class="bi bi-arrow-clockwise"></i> Start New
          </button>
        </div>
        <div class="card-body py-2">
          <div class="row small">
            <div class="col-auto"><strong>Language:</strong> {{ selectedLanguage === 'Other' ? customLanguage : selectedLanguage }}</div>
            <div class="col-auto"><strong>Feature:</strong> {{ selectedFeature }}</div>
            <div class="col-auto" v-if="currentPackage.locations.length">
              <strong>Locations:</strong> {{ currentPackage.locations.map(l => l.location).join(', ') }}
            </div>
            <div class="col-auto" v-if="queryDefn.startdate">
              <strong>Date Range:</strong> {{ queryDefn.startdate }} to {{ queryDefn.enddate || 'present' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Conversation Messages -->
      <div class="messages-container mb-3" ref="messagesContainer">
        <div v-for="(msg, index) in messages" :key="index" class="mb-3">
          <!-- User Message -->
          <div v-if="msg.role === 'user'" class="d-flex justify-content-end">
            <div class="bg-primary text-white rounded-3 p-3 shadow-sm" style="max-width: 80%;">
              <div class="d-flex align-items-start">
                <i class="bi bi-person-circle me-2 fs-5"></i>
                <div class="text-break">{{ msg.content }}</div>
              </div>
            </div>
          </div>
          
          <!-- Assistant Message -->
          <div v-else-if="msg.role === 'assistant'" class="d-flex justify-content-start">
            <div v-if="msg.content" class="bg-light rounded-3 p-3 shadow-sm" style="max-width: 90%;">
              <div class="d-flex align-items-start">
                <i class="bi bi-robot me-2 fs-5 text-dark flex-shrink-0"></i>
                <div class="overflow-hidden" style="min-width: 0;">
                  <!-- Main Answer -->
                  <div v-if="msg.content" class="mb-3">
                    <p class="mb-0 text-break" style="white-space: pre-wrap; word-wrap: break-word;">{{ msg.content }}</p>
                  </div>
                  
                  <!-- Code Block -->
                  <div v-if="msg.code" class="card border-0 shadow-sm mb-3" style="max-width: 100%;">
                    <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center py-2">
                      <span class="badge bg-primary">{{ msg.language || selectedLanguage }}</span>
                      <button class="btn btn-sm btn-light" @click="copyToClipboard(msg.code, $event)">
                        <i class="bi bi-clipboard"></i> Copy
                      </button>
                    </div>
                    <div class="card-body p-0 bg-dark overflow-auto">
                      <pre class="mb-0 p-3 text-light" style="max-width: 100%; overflow-x: auto;"><code :class="'language-' + getPrismLanguage(msg.language || selectedLanguage)" v-html="getHighlightedCode(msg.code, msg.language || selectedLanguage)"></code></pre>
                    </div>
                  </div>
                  
                  <!-- Description -->
                  <div v-if="msg.description" class="alert alert-info py-2 px-3 mb-3">
                    <h6 class="alert-heading fs-6 mb-2">
                      <i class="bi bi-info-circle me-1"></i> Description
                    </h6>
                    <p class="mb-0 small text-break" style="white-space: pre-wrap; word-wrap: break-word;">{{ msg.description }}</p>
                  </div>
                  
                  <!-- Notes -->
                  <div v-if="msg.notes && msg.notes.length" class="alert alert-warning py-2 px-3 mb-3">
                    <h6 class="alert-heading fs-6 mb-2">
                      <i class="bi bi-lightbulb me-1"></i> Notes
                    </h6>
                    <ul class="mb-0 small ps-3">
                      <li v-for="(note, idx) in msg.notes" :key="idx" class="text-break">{{ note }}</li>
                    </ul>
                  </div>
                  
                  <!-- Requirements -->
                  <div v-if="msg.requirements && msg.requirements.length" class="alert alert-secondary py-2 px-3 mb-3">
                    <h6 class="alert-heading fs-6 mb-2">
                      <i class="bi bi-gear me-1"></i> Requirements
                    </h6>
                    <ul class="mb-0 small ps-3">
                      <li v-for="(req, idx) in msg.requirements" :key="idx" class="text-break">{{ req }}</li>
                    </ul>
                  </div>
                  
                  <!-- Sources -->
                  <div v-if="msg.sources && msg.sources.length" class="card border-secondary mb-0">
                    <div class="card-header bg-light bg-opacity-10 py-2">
                      <h6 class="mb-0 fs-6">
                        <i class="bi bi-book me-1"></i> Sources
                      </h6>
                    </div>
                    <div class="card-body py-2">
                      <ul class="mb-0 small ps-3">
                        <li v-for="src in msg.sources" :key="src.url" class="text-break">
                          <a :href="src.url" target="_blank" class="text-decoration-none">
                            {{ src.title || src.filename }}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Thinking Indicator -->
        <div v-if="isThinking" class="d-flex justify-content-start">
          <div class="bg-light rounded-3 p-3 shadow-sm">
            <div class="d-flex align-items-center">
              <i class="bi bi-robot me-2 fs-5 text-dark"></i>
              <div class="d-flex align-items-center">
                <span class="spinner-border spinner-border-sm me-2 text-primary"></span>
                <span class="text-muted">Thinking...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Follow-up Input -->
      <div class="card">
        <div class="card-body">
          <!-- Feedback Section -->
          <div v-if="latestAssistantMsg && !latestAssistantMsg.isStreaming" class="">
            <div v-if="!latestAssistantMsg.feedbackGiven">
              <button class="btn btn-sm text-primary" @click="showFeedback(latestAssistantMsg)">
                <i class="bi bi-chat-dots me-1"></i> Provide feedback
              </button>
            </div>
            <div v-else class="text-muted small">
              <i class="bi bi-check-circle text-success me-1"></i> Thanks for your feedback!
            </div>
          </div>

          <form @submit.prevent="sendFollowUp">
            <div class="input-group">
              <input 
                v-model="followUpMessage" 
                type="text" 
                class="form-control" 
                placeholder="Ask a follow-up question or request modifications..."
                :disabled="isThinking"
              >
              <button class="btn btn-primary" type="submit" :disabled="isThinking || !followUpMessage.trim()">
                <i class="bi bi-send"></i>
              </button>
            </div>
          </form>


        </div>
      </div>
    
    </div>
    <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
      {{ errorMessage }}   
      <button class="btn btn-sm btn-outline-secondary ms-3" @click="errorMessage = ''">
        <i class="bi bi-x-circle me-1"></i>
        Clear
      </button>
    </div>
    <!-- Timeline Query Editor Modal -->
    <TimelineQueryEditor
      v-if="showTimelineQueryEditor"
      :show="showTimelineQueryEditor"
      :queryDefinition="currentPackage.params || {}"
      @save="updateQueryDefn"
      @cancel="showTimelineQueryEditor = false"
    />
    <!-- Feedback Modal -->
    <div class="modal fade" :class="{ 'show d-block': showFeedbackModal }" tabindex="-1" v-if="showFeedbackModal">
     
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-chat-dots me-2"></i>Feedback
            </h5>
            <button type="button" class="btn-close" @click="closeFeedbackModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Did this code work for you?</label>
              <div class="btn-group w-100" role="group">
                <input type="radio" class="btn-check" name="feedback-worked" id="feedback-yes" 
                       v-model="feedbackData.rating" value="5">
                <label class="btn btn-outline-success" for="feedback-yes">
                  <i class="bi bi-hand-thumbs-up me-1"></i> Yes
                </label>
                
                <input type="radio" class="btn-check" name="feedback-worked" id="feedback-no" 
                       v-model="feedbackData.rating" value="1">
                <label class="btn btn-outline-danger" for="feedback-no">
                  <i class="bi bi-hand-thumbs-down me-1"></i> No
                </label>
                
                <input type="radio" class="btn-check" name="feedback-worked" id="feedback-partial" 
                       v-model="feedbackData.rating" value="3">
                <label class="btn btn-outline-warning" for="feedback-partial">
                  <i class="bi bi-exclamation-triangle me-1"></i> Partially
                </label>
              </div>
            </div>
            
            <div class="mb-3">
              <label class="form-label">Additional comments (optional):</label>
              <textarea v-model="feedbackData.comments" class="form-control" rows="3" 
                        placeholder="What worked well? What could be improved?"></textarea>
            </div>

            <div v-if="feedbackData.rating !==5" 
                 class="alert alert-info py-2 px-3 mb-0">
              <small>
                <i class="bi bi-info-circle me-1"></i>
                Your feedback helps us improve. Consider asking a follow-up question to get a better solution.
              </small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeFeedbackModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="submitFeedback" 
                    :disabled="!feedbackData.rating">
              <i class="bi bi-send me-1"></i> Submit Feedback
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showFeedbackModal" class="modal-backdrop fade show" @click="closeFeedbackModal"></div>


  </div>
</template>

<script>
import ContentTypeChooser from '/src/components/ContentTypeChooser.vue';
import UnitGroupDropdown from '../UnitGroupDropdown.vue';
import LocationsEditor from '../LocationsEditor.vue';
import DateRangeChooser from '../DateRangeChooser.vue';
import TimelineQueryEditor from '../modalwrappers/TimelineQueryEditor.vue';
import websiteFiles from '@/utils/website_files_dictionary.js';
import { VcConversion } from "/src/visualcrossing-core/vcconversion";
import "/src/visualcrossing-core/vccommunicator_openai";
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';

// Core language components that others depend on
import 'prismjs/components/prism-markup'; // HTML/XML
import 'prismjs/components/prism-clike'; // C-like syntax base

// Language-specific components (order matters for dependencies)
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-markup-templating'; // Required for PHP
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-r';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-bash'; // For cURL

export default {
  name: 'AiApiAssistant',
  components: {
    ContentTypeChooser,
    UnitGroupDropdown,
    LocationsEditor,
    DateRangeChooser,
    TimelineQueryEditor,
  },
  props: {
    sessionId: String,
    sessionState: Object,
    showQueryDefnBuilder: { type: Boolean, default: true },
    queryDefinition: Object,
    sourceLocations: { 
      type: Array, 
      default: () => [],
      validator: (value) => {
        console.log('sourceLocations prop validator:', value);
        return true; // Always return true for debugging
      }
    },
  },
  data() {
    return {
      errorMessage: '',
      unitGroups: VcConversion.UNITGROUPS,
      userPrompt: '',
      selectedLanguage: 'Python',
      customLanguage: '',
      selectedFeature: 'Fetch Timeline Data',
      parameters: '',
      //location: '',
      startDate: '',
      endDate: '',
      languages: ['Python', 'JavaScript', 'Java', 'C#', 'PHP', 'R', 'Go', 'Ruby', 'PowerQuery (for Microsoft Excel)', 'PowerQuery (for Microsoft Power BI)', 'SQL', 'cURL'],
      apiFeatures: [
        'Fetch Timeline Data',
      ],
      messages: [],
      threadId: null,
      isThinking: false,
      apiKey: (this.sessionState && this.sessionState.apiKey) || "",
      rawPaste: '',
      showOlder: false,
      showTimelineQueryEditor: false,
      currentPackage: {
        params: this.queryDefinition ? { ...this.queryDefinition } : {
          "goal": null,
          "startdate": null,
          "enddate": null,
          "unitGroup": "metric",
          "contentType": "json",
          "options": "",
          "include": "",
        },
        locations: this.sourceLocations || [],
        notificationConfigs: [],
        runOnce: true,
        enabled: true,
        runTimes: [],
        type: 4,
        accountUserId: null
      },
      multiLocationStrategy: 'loop', // New data property for multi-location strategy
      followUpMessage: '', // New data property for follow-up questions
      currentRunId: null, // Track the current run ID
      pollAttempts: 0, // Track polling attempts
      pollingFailed: false, // New flag to track if polling failed
      streamingMessage: null, // Current streaming message
      eventSource: null, // SSE connection
      showFeedbackModal: false,
      feedbackMessageIndex: null,
      feedbackData: {
        rating: 0,
        comments: ''
      }
    };
  },

  computed: {
    vcCommunicator() {
      return window.vcCommunicator || null;
    },
    latestAssistantMsg() {
    // Find the last assistant message in the array
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].role === 'assistant') return this.messages[i];
      }
      return null;
    },
    isOptional() {
      // Check if the latest assistant message is optional
      const latestMsg = this.latestAssistantMsg;
      return latestMsg && latestMsg.content.includes('[OPTIONAL]');
    },
    olderAssistantMsgs() {
      // All previous assistant messages except the latest
      const assistants = this.messages.filter(msg => msg.role === 'assistant');
      return assistants.length > 1 ? assistants.slice(0, assistants.length - 1) : [];
    },
    queryDefn() {
      return this.currentPackage.params || {};
    },
    canRetryFetch() {
      // Streaming doesn't need retry functionality
      return false;
    },
  },
  watch: {
    sessionState(newState) {
      if (newState && newState.apiKey) {
        this.apiKey = newState.apiKey;
      } else {
        this.apiKey = '';
      }
      this.updatePrompt();
    },
    queryDefinition: {
      handler(newVal) {
        if (newVal) {
          console.log("Updating queryDefinition prop:", newVal);
          // Deep clone to avoid mutating the prop
          this.currentPackage.params = { ...newVal };
          // Trigger prompt update
          this.updatePrompt();
        }
      },
      deep: true, // Watch nested properties
      immediate: true // Run on component mount
    },
    sourceLocations: {
      handler(newVal) {
        console.log("Updating sourceLocations prop:", newVal);
        if (Array.isArray(newVal)) {
          this.currentPackage.locations = newVal;
          this.updatePrompt();
        }
      },
      deep: true,
      immediate: true
    },
    selectedLanguage: 'updatePrompt',
    customLanguage: 'updatePrompt',
    selectedFeature: 'updatePrompt',
    parameters: 'updatePrompt',
    //location: 'updatePrompt',
    //startDate: 'updatePrompt',
    //endDate: 'updatePrompt',
    apiKey: 'updatePrompt',
    currentPackage: {
      handler(newVal) {
       this.updatePrompt();
      },
      deep: true
    }
  },
  created() {
     console.log('AiApiAssistant created with sourceLocations:', this.sourceLocations);
 
    if (this.queryDefinition) {
      this.currentPackage.params = { ...this.queryDefinition };
    }
  },
  methods: {
    updateQueryDefn(defn) {
      Object.assign(this.currentPackage.params, defn);
      this.showTimelineQueryEditor = false;
    },
    setUnitGroup(id) {
      var me=this;
      me.queryDefn.unitGroup=id;
     
      
    },
    getUnitGroupLabel(id, short) {
      var unitGroup = this.unitGroups.reduce(function (a, d) {
        return a || (d.id === id && d);
      }, null);
      return (unitGroup && short ? unitGroup.shortlabel : unitGroup.label) || "-";
    },
    updateLocations(locations) {
      this.currentPackage.locations = locations;
    },
    setDateRange(startPeriod, endPeriod) {

      let me = this;

      me.queryDefn.startdate = startPeriod;
      me.queryDefn.enddate = endPeriod;
      //console.log("Set date range:"+defn.startdate +"=> "+defn.enddate);

    },
    clearThread() {
      if (confirm('Are you sure you want to clear this conversation?')) {
        this.threadId = null;
        this.messages = [];
      }
    },
    async sendMessage() {
      // Use streaming version
      //this.threadId = "foo_thread";
      return this.sendMessageStreaming();
    },
   // Update the sendMessageStreaming method
  async sendMessageStreaming() {
    //const prompt = this.generatePrompt(this.userPrompt);
    const prompt = this.userPrompt;
    if (!prompt) return;

    if (!this.vcCommunicator) {
      console.error('VcCommunicator not available');
      return;
    }

    this.messages.push({ role: 'user', content: prompt, language: this.selectedLanguage });
    this.isThinking = true;
    this.pollingFailed = false;
    
    // Create a placeholder for the streaming message
    this.streamingMessage = {
      role: 'assistant',
      content: '',
      code: '',
      language: this.selectedLanguage,
      sources: [],
      notes: [],
      requirements: [],
      description: '',
      isStreaming: true
    };
    this.messages.push(this.streamingMessage);

    try {
   
      
      this.eventSource = this.vcCommunicator.streamaimessage(
        this.threadId,
        prompt,
        'asst_W5EtNiQDn6Kw4IoXTzIBfxY0',
        
        // onChunk callback
        (chunkResponse) => {
          this.threadId = chunkResponse && (chunkResponse.thread_id || this.threadId);
          Object.assign(this.streamingMessage, {
            content: chunkResponse.main_answer || '',
            code: chunkResponse.code || '',
            language: chunkResponse.language || this.selectedLanguage,
            description: chunkResponse.description || '',
            notes: chunkResponse.notes || [],
            requirements: chunkResponse.requirements || []
          });
          // Re-highlight code blocks
          this.$nextTick(() => {
            Prism.highlightAll();
          });
        },
        
        // onComplete callback
        (finalResponse) => {
          this.threadId = finalResponse && (finalResponse.thread_id || this.threadId);
          
          // Mark streaming as complete
          this.streamingMessage.isStreaming = false;
          
          // Update with final data
          Object.assign(this.streamingMessage, {
            content: finalResponse.main_answer || '',
            code: finalResponse.code || '',
            language: finalResponse.language || this.selectedLanguage,
            description: finalResponse.description || '',
            notes: finalResponse.notes || [],
            requirements: finalResponse.requirements || []
          });
          
          this.isThinking = false;
          this.streamingMessage = null;
          
          // Re-highlight code blocks
          this.$nextTick(() => {
            Prism.highlightAll();
          });
        },
        
        // onError callback
        (error) => {
          console.error('Streaming error:', error);
          if (this.streamingMessage) {  
            this.streamingMessage.isStreaming = false;
            this.streamingMessage.content = 'An error occurred: ' + (error.message || 'Unknown error during streaming');
          }
          if (!this.threadId) {
            this.errorMessage = 'An error occurred: ' + (error.message || 'Unknown error during streaming');
          }
          this.isThinking = false;
          this.streamingMessage = null;
        }
      );
      
    } catch (e) {
      console.error(e);
      // Remove the streaming message if it exists
      if (this.streamingMessage) {
        const index = this.messages.indexOf(this.streamingMessage);
        if (index > -1) {
          this.messages.splice(index, 1);
        }
      }
      
      this.messages.push({
        role: 'assistant',
        content: 'An error occurred: ' + e.message,
        code: '',
        sources: []
      });
      this.isThinking = false;
      this.streamingMessage = null;
    }
  },

  
    
  generatePrompt(extraInstructions = '') {
      let lang = this.selectedLanguage === 'Other' ? this.customLanguage : this.selectedLanguage;
      let prompt = `Generate a code sample in ${lang} using the Visual Crossing Weather API. `;
      
      const locationsArr = this.currentPackage.locations;
      let locationList = [];
      if (locationsArr && locationsArr.length > 0) {
        locationList = locationsArr.map(locObj => locObj.location).filter(Boolean);
        if (locationList.length > 1) {
          if (this.multiLocationStrategy === 'timelinemulti') {
            prompt += `Use the timelinemulti endpoint for multiple locations. Locations: ${locationList.join(", ")}. `;
          } else {
            prompt += `Generate code that loops through each location and calls the single location timeline API for each. Locations: ${locationList.join(", ")}. `;
          }
        } else if (locationList.length === 1) {
          prompt += `Location: ${locationList[0]}. `;
        }
      }
      
      if (this.selectedFeature) {
        prompt += `The sample should demonstrate how to "${this.selectedFeature}". `;
      }
      
      if (this.queryDefn.startdate) {
        prompt += `Start date: ${this.currentPackage.params.startdate}. `;
      }
      
      if (this.queryDefn.enddate) {
        prompt += `End date: ${this.currentPackage.params.enddate}. `;
      }
      
      if (this.queryDefn.unitGroup) {
        prompt += `Unit group: ${this.queryDefn.unitGroup}. `;
      }
      
      // Add any other properties from queryDefn not already included
      const usedProps = ['startdate', 'enddate', 'unitGroup'];
      let hasAdvancedOptions = false;
      Object.keys(this.queryDefn).forEach(key => {
        if (!usedProps.includes(key) && this.queryDefn[key] !== null && this.queryDefn[key] !== '') {
          if (!hasAdvancedOptions) {
            prompt += 'Advanced options: ';
            hasAdvancedOptions = true;
          }
          prompt += `'${key}':'${this.queryDefn[key]}'. `;
        }
      });

      if (this.apiKey) {
        prompt += `API Key: ${this.apiKey}. `;
      }
      
      if (this.parameters) {
        prompt += `Use these parameters: ${this.parameters}. `;
      }
      
      if (extraInstructions && extraInstructions !== prompt.trim()) {
        prompt += `Additional instructions: ${extraInstructions}`;
      }
      
      // Remove the formatting instructions since they're now in the system prompt
      return prompt.trim();
    },

  updatePrompt() {
    this.userPrompt = this.generatePrompt();
  },
  async pollForCompletion(runId, maxAttempts = 20) {
      const startAttempts = this.pollAttempts;
      const attemptsLimit = startAttempts + maxAttempts;
      
      while (this.pollAttempts < attemptsLimit) {
        this.pollAttempts++;
        
        try {
          const runStatus = await this.vcCommunicator.getairunstatus(this.threadId, runId);

          if (runStatus.errorCode) {
            throw new Error(runStatus.message || 'Failed to get run status');
          }

          if (runStatus.status === 'completed') break;
          if (runStatus.status === 'failed') throw new Error('Assistant run failed');

          await new Promise((resolve) => setTimeout(resolve, 3000));
        } catch (e) {
          // Network error during polling
          console.error('Polling error:', e);
          throw new Error('Request timeout - click "Check for response" to retry');
        }
      }

      if (this.pollAttempts >= attemptsLimit) {
        throw new Error('Request timeout - click "Check for response" to retry');
      }

      // Get messages
      const msgData = await this.vcCommunicator.getaimessages(this.threadId, null);

      if (msgData.errorCode) {
        throw new Error(msgData.message || 'Failed to get messages');
      }

      // Find the latest assistant message
      const messages = msgData.messages || msgData.data || [];
      const assistantMsg = messages.find((msg) => msg.role === 'assistant');

      if (!assistantMsg) {
        throw new Error('No assistant response found');
      }

      // Extract the text content and annotations
      let textContent = '';
      let annotations = [];

      if (assistantMsg.content && assistantMsg.content[0]) {
        if (assistantMsg.content[0].text) {
          textContent = assistantMsg.content[0].text.value || '';
          annotations = assistantMsg.content[0].text.annotations || [];
        } else if (typeof assistantMsg.content[0] === 'string') {
          textContent = assistantMsg.content[0];
        }
      } else if (typeof assistantMsg.content === 'string') {
        textContent = assistantMsg.content;
      }

      // Try to parse as JSON first
      try {
        const parsedContent = JSON.parse(textContent);
        const sources = this.processAnnotations(annotations);
        
        // Handle the new response format
        if (parsedContent.code) {
          return {
            main_answer: parsedContent.main_answer || '',
            content: parsedContent.main_answer || '', // For display
            code: parsedContent.code,
            language: parsedContent.language || this.selectedLanguage,
            description: parsedContent.description || '',
            sources: sources,
            notes: parsedContent.notes || [],
            requirements: parsedContent.requirements || []
          };
        }
        
        return {
          ...parsedContent,
          content: parsedContent.main_answer || parsedContent.text || '',
          sources: sources
        };
      } catch {
        // If not JSON, return as plain text
        return {
          content: textContent || assistantMsg.content,
          sources: []
        };
      }
    },
    processAnnotations(annotations) {
      const sources = [];
      annotations.forEach(annotation => {
        if (annotation.type === 'file_citation' && annotation.file_citation) {
          const fileId = annotation.file_citation.file_id;
          const fileDetails = websiteFiles.lookup(fileId);
          if (fileDetails) {
            sources.push({
              citation: annotation.text,
              fileId: fileId,
              url: fileDetails.source,
              title: fileDetails.title,
              filename: fileDetails.filename
            });
          } else {
            sources.push({
              citation: annotation.text,
              fileId: fileId,
              url: '#',
              title: 'Unknown source',
              filename: 'Unknown file'
            });
          }
        }
      });
      // Remove duplicates based on fileId
      const uniqueSources = sources.filter((source, index, self) =>
        index === self.findIndex((s) => s.fileId === source.fileId)
      );
      return uniqueSources;
    },
    getPrismLanguage(language) {
      const languageMap = {
        'python': 'python',
        'javascript': 'javascript',
        'java': 'java',
        'c#': 'csharp',
        'php': 'php',
        'r': 'r',
        'go': 'go',
        'ruby': 'ruby',
        'sql': 'sql',
        'curl': 'bash',
        'powerquery in microsoft excel': 'javascript',
        'powerquery in microsoft power bi': 'javascript'
      };
      
      // Convert to lowercase for case-insensitive matching
      const normalizedLang = language.toLowerCase();
      return languageMap[normalizedLang] || 'javascript';
    },
    getHighlightedCode(code, language) {
      if (!code) return '';
      
      // Map languages to Prism language codes (case-insensitive)
      const languageMap = {
        'python': 'python',
        'javascript': 'javascript',
        'java': 'java',
        'c#': 'csharp',
        'php': 'php',
        'r': 'r',
        'go': 'go',
        'ruby': 'ruby',
        'sql': 'sql',
        'curl': 'bash',
        'powerquery in microsoft excel': 'javascript',
        'powerquery in microsoft power bi': 'javascript'
      };
      
      // Convert to lowercase for case-insensitive matching
      const normalizedLang = language.toLowerCase();
      const prismLang = languageMap[normalizedLang] || 'javascript';
      
      try {
        // Check if the language is supported
        if (!Prism.languages[prismLang]) {
          console.warn(`Language '${prismLang}' not supported by Prism, falling back to plain text`);
          return this.escapeHtml(code);
        }
        
        // Use Prism to highlight the code
        const highlighted = Prism.highlight(code, Prism.languages[prismLang], prismLang);
        return highlighted;
      } catch (e) {
        // Fallback to plain text if highlighting fails
        console.error('Syntax highlighting failed:', e);
        return this.escapeHtml(code);
      }
    },
    
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    },
    
    copyToClipboard(code) {
      if (!code) return;
      
      // Get the button from the click event
      const button = event?.target?.closest('button');
      
      navigator.clipboard.writeText(code).then(() => {
        // Only show visual feedback if we have a button reference
        if (button) {
          const originalHtml = button.innerHTML;
          button.innerHTML = '<i class="bi bi-check"></i> Copied!';
          button.classList.add('btn-success');
          button.classList.remove('btn-light');
          
          setTimeout(() => {
            button.innerHTML = originalHtml;
            button.classList.remove('btn-success');
            button.classList.add('btn-light');
          }, 2000);
        }
      }).catch((err) => {
        console.error('Failed to copy:', err);
        // Fallback method for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          // Show success if we have a button
          if (button) {
            const originalHtml = button.innerHTML;
            button.innerHTML = '<i class="bi bi-check"></i> Copied!';
            button.classList.add('btn-success');
            button.classList.remove('btn-light');
            
            setTimeout(() => {
              button.innerHTML = originalHtml;
              button.classList.remove('btn-success');
              button.classList.add('btn-light');
            }, 2000);
          }
        } catch (err) {
          alert('Failed to copy code. Please try selecting and copying manually.');
        } finally {
          document.body.removeChild(textArea);
        }
      });
    },

    // Update sendFollowUp to use streaming
    async sendFollowUp() {
      const message = this.followUpMessage.trim();
      if (!message || !this.vcCommunicator) return;

      this.messages.push({ role: 'user', content: message });
      this.followUpMessage = '';
      this.isThinking = true;
      this.pollingFailed = false;
      
      // Create a placeholder for the streaming message
      this.streamingMessage = {
        role: 'assistant',
        content: '',
        code: '',
        language: this.selectedLanguage,
        sources: [],
        notes: [],
        requirements: [],
        description: '',
        isStreaming: true
      };
      this.messages.push(this.streamingMessage);

      try {
        
        this.eventSource = this.vcCommunicator.streamaimessage(
          this.threadId,
          message,
          'asst_W5EtNiQDn6Kw4IoXTzIBfxY0',
          
          // onChunk callback
          (chunkResponse) => {
            // Update with final data
            this.threadId = chunkResponse && (chunkResponse.thread_id || this.threadId);
            Object.assign(this.streamingMessage, {
              content: chunkResponse.main_answer || '',
              code: chunkResponse.code || '',
              language: chunkResponse.language || this.selectedLanguage,
              description: chunkResponse.description || '',
              notes: chunkResponse.notes || [],
              requirements: chunkResponse.requirements || []
            });
              
            // Re-highlight code blocks
            this.$nextTick(() => {
              Prism.highlightAll();
            });
          },
          
          // onComplete callback
          (finalResponse) => {
            // Mark streaming as complete
            this.streamingMessage.isStreaming = false;
            this.threadId = finalResponse && (finalResponse.thread_id || this.threadId);
            // Update with final data
            Object.assign(this.streamingMessage, {
              content: finalResponse.main_answer || '',
              code: finalResponse.code || '',
              language: finalResponse.language || this.selectedLanguage,
              description: finalResponse.description || '',
              notes: finalResponse.notes || [],
              requirements: finalResponse.requirements || []
            });
            
            this.isThinking = false;
            this.streamingMessage = null;
            
            // Re-highlight code blocks
            this.$nextTick(() => {
              Prism.highlightAll();
            });
          },
          
          // onError callback
          (error) => {
            console.error('Streaming error:', error);
            if (this.streamingMessage) {  
              this.streamingMessage.isStreaming = false;
              this.streamingMessage.content = 'An error occurred: ' + (error.message || 'Unknown error during streaming');
            }
            if (!this.threadId) {
              this.errorMessage = 'An error occurred: ' + (error.message || 'Unknown error during streaming');
            }
            this.isThinking = false;
            this.streamingMessage = null;
          }
        );
        
      } catch (e) {
        console.error(e);
        // Remove the streaming message if it exists
        if (this.streamingMessage) {
          const index = this.messages.indexOf(this.streamingMessage);
          if (index > -1) {
            this.messages.splice(index, 1);
          }
        }
        
        this.messages.push({
          role: 'assistant',
          content: 'An error occurred: ' + e.message,
          code: '',
          sources: []
        });
        this.isThinking = false;
        this.streamingMessage = null;
      }
    },
    showFeedback(msg) {
      // Open the feedback modal for the given message index
      //const msg = this.messages[index];
      if (msg && msg.role === 'assistant') {
        this.currentFeedbackIndex = this.messages.indexOf(msg);
        this.feedbackData.rating = 0;
        this.feedbackData.comments = '';
        this.showFeedbackModal = true;
      }
    },
    closeFeedbackModal() {
      this.showFeedbackModal = false;
    },
    async submitFeedback() {
      if (this.feedbackData.rating === 0) return;

      const msg = this.messages[this.currentFeedbackIndex];
      if (!msg) return;
      
      // Send feedback to the server or process it as needed
      // For now, we'll just log it
      console.log('Feedback submitted:', {
        messageId: msg.id,
        rating: this.feedbackData.rating,
        comments: this.feedbackData.comments
      });
      this.vcCommunicator.submitfeedback("AiApiAssistant", this.feedbackData.rating, this.feedbackData.comments+"\nAPI AI Thread ID: "+this.threadId);
      // Mark the message as having feedback
      this.messages[this.currentFeedbackIndex] = {
        ...msg,
        feedbackGiven: true
      };
      
      this.closeFeedbackModal();
    }
  },
  mounted() {
    console.log('AiApiAssistant mounted with sourceLocations:', this.sourceLocations);
 
    // Populate the prompt textbox on initial load
    this.updatePrompt();

    
  },
  updated() {
    // Re-run Prism highlighting for any new code blocks
    this.$nextTick(() => {
      Prism.highlightAll();
    });
  },
  beforeUnmount() {
    if (this.eventSource) {
      this.eventSource.close();
    }
  },
};
</script>

<style scoped>
/* Minimal custom styles - mostly using Bootstrap utilities */
.messages-container {
  max-height: 600px;
  overflow-y: auto;
  padding: 1rem;
}

/* Only need to style the Prism code blocks */
pre code {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
}

/* Ensure proper code block styling */
.bg-dark pre {
  background-color: #1e1e1e !important;
}

/* Small enhancement for scrollbar */
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Modal styles */
.modal-content {
  border-radius: 0.5rem;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
}
</style>
